import { PHASE } from './phases.js';
import { ACTION } from './actions.js';
import { createInitialState } from './initialState.js';
import { configForState } from './modeConfig.js';
import { removePortfolioInstances } from '../engine/advancedGame.js';
import {
  createRng,
  deriveSeed,
  rollVariableOutputs,
  drawDisasters,
  rollWorldEvents,
  assignRiskTiers,
  tierWeightsToCardWeights,
  solveTierWeights,
  resolvePersistentDisasterTarget
} from '../engine/index.js';
import { EFFECT_KIND, TARGET_KIND } from '../data/disasters.js';
import { COP30_OUTCOMES } from '../data/cop30.js';
import {
  GAME_YEARS,
  DISASTER_DRAWS_PER_YEAR
} from '../data/constants.js';

function newSlotsThisYear(state) {
  // Per-mode: Advanced adds a fourth Year 1 slot to pay for the two the
  // legacy fleet occupies (section 16.15), so the fleet's slots are
  // charged against Year 1 and the net free count is 2. Classic starts
  // empty and is unchanged.
  // Plants already on the grid entering Year 1 occupy their slots; in a
  // real game that is exactly the starting fleet. Later years grant
  // their own slots, the fleet's having already been charged.
  const config = configForState(state);
  const held = state.year === 1 ? state.portfolio.length : 0;
  return Math.max(0, (config.slotsNewPerYear[state.year] ?? 0) - held);
}

function slotsAvailableThisYear(state) {
  // Retiring a plant frees its grid connection slot for the same
  // build phase (section 16.11). pendingRetirements is always empty
  // outside Advanced Mode.
  return newSlotsThisYear(state) + state.pendingRetirements.length;
}

function computeRiskOutlookForYear(state, year) {
  const { disasterById } = configForState(state);
  const deckObjs = state.disasterDeck.map((id) => disasterById[id]);
  if (year <= 1 || deckObjs.length < 4) return {};
  const rng = createRng(deriveSeed(state.seed, `year-${year}-risk-outlook`));
  return assignRiskTiers(deckObjs, rng);
}

function hasCompleteRiskOutlook(tierByCardId, deckObjs) {
  if (!tierByCardId || Object.keys(tierByCardId).length === 0) return false;
  return deckObjs.every((card) => tierByCardId[card.id]);
}

function startYearState(state, year) {
  const riskTierByDisasterId = computeRiskOutlookForYear(state, year);
  return {
    ...state,
    phase: PHASE.AWAITING_SUBSIDY_REVEAL,
    year,
    pendingBuilds: [],
    pendingRetirements: [],
    rolledOutputs: null,
    drawnDisasterIds: [],
    severityByDisasterId: {},
    riskTierByDisasterId,
    subsidyReformedThisYear: false
  };
}

export function gameReducer(state, action) {
  switch (action.type) {
    case ACTION.START_GAME:
    case ACTION.RESTART: {
      const seed = action.seed ?? state.seed;
      const mode = action.mode ?? state.mode;
      const fresh = createInitialState({ seed, mode });
      return startYearState(fresh, 1);
    }

    case ACTION.ACKNOWLEDGE_SUBSIDY: {
      if (state.phase !== PHASE.AWAITING_SUBSIDY_REVEAL) return state;
      return { ...state, phase: PHASE.BUILDING };
    }

    case ACTION.SELECT_PLANT: {
      if (state.phase !== PHASE.BUILDING) return state;
      // Buildable, not merely known: the legacy fleet resolves by id
      // but is never on the menu, and it costs no capital.
      if (!configForState(state).buildablePlantIds.has(action.plantId)) {
        return state;
      }
      if (state.pendingBuilds.length >= slotsAvailableThisYear(state)) {
        return state;
      }
      return {
        ...state,
        pendingBuilds: [...state.pendingBuilds, action.plantId]
      };
    }

    case ACTION.UNSELECT_PLANT: {
      if (state.phase !== PHASE.BUILDING) return state;
      const i = action.index;
      if (!Number.isInteger(i) || i < 0 || i >= state.pendingBuilds.length) {
        return state;
      }
      const next = state.pendingBuilds.slice();
      next.splice(i, 1);
      return { ...state, pendingBuilds: next };
    }

    case ACTION.SELECT_RETIREMENT: {
      // Advanced Capacity Mode only (section 16.11).
      if (state.phase !== PHASE.BUILDING) return state;
      if (!configForState(state).supportsRetirement) return state;
      const owned = state.portfolio.filter(
        (id) => id === action.plantId
      ).length;
      const alreadyRetiring = state.pendingRetirements.filter(
        (id) => id === action.plantId
      ).length;
      if (owned - alreadyRetiring <= 0) return state;
      return {
        ...state,
        pendingRetirements: [...state.pendingRetirements, action.plantId]
      };
    }

    case ACTION.UNSELECT_RETIREMENT: {
      if (state.phase !== PHASE.BUILDING) return state;
      const i = action.index;
      if (
        !Number.isInteger(i) ||
        i < 0 ||
        i >= state.pendingRetirements.length
      ) {
        return state;
      }
      // Cancelling a retirement takes its freed slot back; blocked
      // while a pending build occupies it (remove the build first).
      const slotsAfter =
        newSlotsThisYear(state) + state.pendingRetirements.length - 1;
      if (state.pendingBuilds.length > slotsAfter) return state;
      const next = state.pendingRetirements.slice();
      next.splice(i, 1);
      return { ...state, pendingRetirements: next };
    }

    case ACTION.COMMIT_BUILD: {
      if (state.phase !== PHASE.BUILDING) return state;
      const newlyBuilt = state.pendingBuilds;
      // Retirement resolves at commit, before new builds join the
      // portfolio. Each retirement freed one slot for this build
      // phase (section 16.11).
      const { portfolio: afterRetirement, removed } =
        removePortfolioInstances(state.portfolio, state.pendingRetirements);
      return {
        ...state,
        portfolio: [...afterRetirement, ...newlyBuilt],
        buildPlanByYear: {
          ...state.buildPlanByYear,
          [state.year]: newlyBuilt
        },
        retirePlanByYear: {
          ...state.retirePlanByYear,
          [state.year]: removed
        },
        pendingBuilds: [],
        pendingRetirements: [],
        phase: PHASE.AWAITING_VARIABLE_ROLLS
      };
    }

    case ACTION.REVEAL_VARIABLE_ROLLS: {
      if (state.phase !== PHASE.AWAITING_VARIABLE_ROLLS) return state;
      if (state.rolledOutputs !== null) return state;
      const rng = createRng(
        deriveSeed(state.seed, `year-${state.year}-rolls`)
      );
      const rolledOutputs = rollVariableOutputs(
        state.portfolio,
        configForState(state).plantsById,
        rng
      );
      return { ...state, rolledOutputs };
    }

    case ACTION.ACK_VARIABLE_ROLLS: {
      if (state.phase !== PHASE.AWAITING_VARIABLE_ROLLS) return state;
      if (state.rolledOutputs === null) return state;
      return { ...state, phase: PHASE.AWAITING_DISASTER };
    }

    case ACTION.REVEAL_DISASTERS: {
      if (state.phase !== PHASE.AWAITING_DISASTER) return state;
      const rng = createRng(
        deriveSeed(state.seed, `year-${state.year}-disasters`)
      );
      const { disasterById } = configForState(state);
      const deckObjs = state.disasterDeck.map((id) => disasterById[id]);
      const drawCount = DISASTER_DRAWS_PER_YEAR[state.year];
      let weightByCardId = null;
      let tierByCardId = {};
      if (state.year > 1 && deckObjs.length >= 4) {
        tierByCardId = hasCompleteRiskOutlook(
          state.riskTierByDisasterId,
          deckObjs
        )
          ? state.riskTierByDisasterId
          : assignRiskTiers(deckObjs, rng);
        const tierWeights = solveTierWeights({
          poolSize: deckObjs.length,
          draws: drawCount
        });
        weightByCardId = tierWeightsToCardWeights({
          pool: deckObjs,
          tierByCardId,
          tierWeights
        });
      }
      const { drawn, remaining } = drawDisasters(
        deckObjs,
        drawCount,
        rng,
        weightByCardId
      );
      const subsidyReformed = drawn.some(
        (d) => d.effect.kind === EFFECT_KIND.SUBSIDY_REMOVE
      );
      const severityByDisasterId = {};
      for (const d of drawn) {
        if (d.severityRoll) severityByDisasterId[d.id] = d.severityRoll;
      }
      return {
        ...state,
        drawnDisasterIds: drawn.map((d) => d.id),
        disasterDeck: remaining.map((d) => d.id),
        severityByDisasterId,
        riskTierByDisasterId: tierByCardId,
        subsidyActive: state.subsidyActive && !subsidyReformed,
        subsidyReformedThisYear: subsidyReformed,
        phase: PHASE.RESOLVING
      };
    }

    case ACTION.RESOLVE_YEAR: {
      if (state.phase !== PHASE.RESOLVING) return state;
      const config = configForState(state);
      const newlyBuilt = state.buildPlanByYear[state.year] ?? [];
      const drawn = state.drawnDisasterIds.map((id) => {
        const card = config.disasterById[id];
        const severityRoll = state.severityByDisasterId?.[id];
        if (severityRoll && card.twoTierSeverity) {
          return {
            ...card,
            effect: card.severity[severityRoll],
            severityRoll,
            isSevere: severityRoll === 'severe'
          };
        }
        return card;
      });
      const targetRng = createRng(
        deriveSeed(state.seed, `year-${state.year}-targets`)
      );
      const resolvedDrawn = drawn.map((d) =>
        resolvePersistentDisasterTarget({
          disaster: d,
          portfolio: state.portfolio,
          plantsById: config.plantsById,
          rng: targetRng
        })
      );
      const priorPersistentDisasters = Object.entries(
        state.persistentDisasterTargets ?? {}
      ).map(([id, family]) => ({
        ...config.disasterById[id],
        target: { kind: TARGET_KIND.FAMILY, family },
        persistentTargetFamily: family
      }));
      const nextPersistentDisasterTargets = {
        ...(state.persistentDisasterTargets ?? {})
      };
      for (const d of resolvedDrawn) {
        if (
          d.effect.kind === EFFECT_KIND.OUTPUT_MULTIPLIER &&
          d.effect.persistent &&
          d.persistentTargetFamily
        ) {
          nextPersistentDisasterTargets[d.id] = d.persistentTargetFamily;
        }
      }
      const lastResult = state.yearResults[state.yearResults.length - 1];
      const priorPlayerBuilds = Object.entries(state.buildPlanByYear)
        .filter(([year]) => Number(year) < state.year)
        .flatMap(([, builds]) => builds ?? []);
      const result = config.resolveYear({
        year: state.year,
        portfolio: [...state.portfolio],
        newlyBuiltThisYear: newlyBuilt,
        priorPlayerBuilds,
        subsidyActive: state.subsidyActive,
        disasters: [...resolvedDrawn, ...priorPersistentDisasters],
        rolledOutputs: state.rolledOutputs ?? {},
        plantsById: config.plantsById,
        rng: targetRng,
        // Advanced Capacity Mode only; Classic resolveYear ignores
        // these (section 16.11 retirement fee, 16.5.2 carryover).
        retiredThisYear: state.retirePlanByYear?.[state.year] ?? [],
        carriedCharge: lastResult?.carriedChargeForNextYear ?? {},
        carriedChargeUnits: lastResult?.carriedChargeUnitsForNextYear ?? {}
      });
      return {
        ...state,
        persistentDisasterTargets: nextPersistentDisasterTargets,
        yearResults: [
          ...state.yearResults,
          { ...result, subsidyReformed: state.subsidyReformedThisYear }
        ],
        phase: PHASE.BETWEEN_YEARS
      };
    }

    case ACTION.ADVANCE_YEAR: {
      if (state.phase !== PHASE.BETWEEN_YEARS) return state;
      if (state.year >= GAME_YEARS) {
        return { ...state, phase: PHASE.COP30 };
      }
      return startYearState(state, state.year + 1);
    }

    case ACTION.REVEAL_COP30: {
      if (state.phase !== PHASE.COP30) return state;
      if (state.cop30) return state;
      const worldRng = createRng(deriveSeed(state.seed, 'world'));
      const { cop30 } = rollWorldEvents(worldRng, COP30_OUTCOMES);
      return { ...state, cop30 };
    }

    case ACTION.FINALISE_GAME: {
      if (state.phase !== PHASE.COP30) return state;
      if (!state.cop30) return state;
      return {
        ...state,
        finalResult: configForState(state).computeFinalResult(state),
        phase: PHASE.GAME_OVER
      };
    }

    default:
      return state;
  }
}
